import { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { publisherApi } from '../api';
import { CATALOG_GROUPS } from '../kindSchemas';
import type { PublisherFormDraft } from '../PublisherHub';
import type { AttachmentItem, ContentCreatePayload, ContentMetaPayload } from '../types';
import { validateHashtagCount } from './HashtagEditor';
import { PublisherGroups } from './PublisherGroups';
import { PublisherPreview } from './PublisherPreview';

interface CatalogPublisherProps {
  onSuccess: (message?: string) => void;
  lockedKind?: string;
  initialDraft?: PublisherFormDraft;
  onDraftChange?: (patch: PublisherFormDraft) => void;
}

function draftValues(draft?: PublisherFormDraft): Record<string, string> {
  return {
    title: draft?.title || '',
    summary: draft?.summary || '',
    sourceUrl: draft?.officialUrl || '',
    ...(draft?.meta || {}),
  };
}

export const CatalogPublisher = ({
  onSuccess,
  lockedKind,
  initialDraft,
  onDraftChange,
}: CatalogPublisherProps) => {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const { notify } = useToast();
  const isOwner = hasRole('SYSTEM_OWNER');
  const [kind, setKind] = useState(lockedKind || 'COMPETITION');
  const [values, setValues] = useState<Record<string, string>>(() => draftValues(initialDraft));
  const [hashtags, setHashtags] = useState<string[]>(initialDraft?.hashtags || []);
  const [fileMap, setFileMap] = useState<Record<string, AttachmentItem[]>>({});
  const [previewing, setPreviewing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const sending = useRef(false);
  const savedContent = useRef<{ id: string; payload: string } | null>(null);

  useEffect(() => {
    if (lockedKind) setKind(lockedKind);
  }, [lockedKind]);

  const groups = useMemo(() => CATALOG_GROUPS[kind] || CATALOG_GROUPS.COMPETITION, [kind]);

  const collectedFiles = (map = fileMap) => Object.values(map).flat();

  const emit = (
    nextValues: Record<string, string>,
    nextTags = hashtags,
    nextFiles = collectedFiles()
  ) => {
    onDraftChange?.({
      title: nextValues.title,
      summary: nextValues.summary,
      officialUrl: nextValues.sourceUrl,
      hashtags: nextTags,
      attachments: nextFiles,
      meta: nextValues,
    });
  };

  const updateValue = (name: string, value: string) => {
    setValues((prev) => {
      const next = { ...prev, [name]: value };
      emit(next);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent, isDraft: boolean) => {
    e.preventDefault();
    if (sending.current) return;
    const title = (values.title || '').trim();
    const summary = (values.summary || title).trim();
    if (!title || !summary) {
      notify('Заполните название и описание');
      return;
    }
    if (!isDraft && !values.sourceUrl) {
      notify('Для публичного каталога обязателен официальный источник');
      return;
    }
    if (!isDraft && kind === 'COMPETITION' && !values.deadline) {
      notify('Для конкурса обязателен дедлайн');
      return;
    }
    if (!isDraft && validateHashtagCount(hashtags)) {
      notify(t('publisher.validation.tags.min' as any));
      return;
    }

    const meta: ContentMetaPayload = {
      level: values.level || undefined,
      format_type: values.format || undefined,
      cost_type: values.costType || undefined,
      cost_amount: values.amount || undefined,
      direction: values.direction || undefined,
      access_type: values.access || undefined,
      publisher_name: values.publisher || undefined,
      publish_year: values.publicationMeta || undefined,
      conditions_text: values.conditions || undefined,
      contact_email: values.contact || undefined,
      salary_text: values.salary || undefined,
      competition_kind: kind === 'COMPETITION' ? values.subtype : undefined,
      age_range: values.ageRange || undefined,
      employment_type: values.employmentType || undefined,
      participant_group: values.participants || undefined,
      city: values.location || undefined,
    };

    sending.current = true;
    setLoading(true);
    setSubmitError('');
    try {
      const payload: ContentCreatePayload = {
        kind,
        subtype: values.subtype || undefined,
        title,
        summary,
        organization: values.organization || undefined,
        category: values.category || undefined,
        official_url: values.sourceUrl || undefined,
        official_source_label: values.sourceLabel || undefined,
        language: values.language || undefined,
        attachments: collectedFiles(),
        event_start: values.eventDate || undefined,
        deadline: values.deadline || undefined,
        feed_eligible: false,
        hashtags,
        meta,
      };
      const serialized = JSON.stringify(payload);
      const saved = savedContent.current;
      const res = saved
        ? saved.payload === serialized
          ? saved
          : await publisherApi.updateContent(saved.id, payload)
        : await publisherApi.createContent(payload);
      savedContent.current = { id: res.id, payload: serialized };
      if (!isDraft) {
        if (isOwner) {
          await publisherApi.publishContent(res.id);
          onSuccess(t('publisher.toast.published' as any));
        } else {
          await publisherApi.submitForModeration(res.id);
          onSuccess(t('publisher.toast.submitted' as any));
        }
      } else {
        onSuccess(t('publisher.toast.draftSaved' as any));
      }
    } catch {
      setSubmitError(savedContent.current
        ? 'Материал сохранён, но отправку завершить не удалось. Повторите попытку или вернитесь к редактированию.'
        : 'Не удалось сохранить материал. Проверьте соединение и повторите попытку. Введённые данные остались в форме.');
    } finally {
      sending.current = false;
      setLoading(false);
    }
  };

  const openPreview = (event: React.FormEvent) => {
    event.preventDefault();
    const title = (values.title || '').trim();
    const summary = (values.summary || title).trim();
    if (!title || !summary) {
      notify('Заполните название и описание');
      return;
    }
    if (!values.sourceUrl) {
      notify('Для публичного каталога обязателен официальный источник');
      return;
    }
    if (kind === 'COMPETITION' && !values.deadline) {
      notify('Для конкурса обязателен дедлайн');
      return;
    }
    if (validateHashtagCount(hashtags)) {
      notify(t('publisher.validation.tags.min' as any));
      return;
    }
    setSubmitError('');
    setPreviewing(true);
  };

  if (previewing) {
    return (
      <PublisherPreview
        badge={t(`publisher.kind.${kind}` as any) || kind}
        accent="#e2ad3a"
        title={(values.title || '').trim()}
        summary={(values.summary || '').trim()}
        sourceUrl={values.sourceUrl}
        tags={hashtags}
        attachments={collectedFiles()}
        rows={[
          ['Организация', values.organization],
          ['Направление', values.category || values.direction],
          ['Дедлайн', values.deadline],
        ].filter((row): row is [string, string] => Boolean(row[1]))}
        confirmLabel={
          isOwner ? t('publisher.action.publishNow' as any) : t('publisher.action.submitModeration' as any)
        }
        busy={loading}
        error={submitError}
        onEdit={() => setPreviewing(false)}
        onConfirm={() => {
          void handleSubmit({ preventDefault() {} } as React.FormEvent, false);
        }}
      />
    );
  }

  return (
    <form style={{ display: 'flex', flexDirection: 'column', gap: 16 }} onSubmit={openPreview}>
      <div className="rg-chip-row">
        <span className="rg-chip is-amber">{t(`publisher.kind.${kind}` as any) || kind}</span>
        <span className="rg-chip">Публичный каталог</span>
      </div>
      <PublisherGroups
        groups={groups}
        values={values}
        onChange={updateValue}
        hashtags={hashtags}
        files={fileMap}
        onFiles={(name, next) => {
          setFileMap((prev) => {
            const map = { ...prev, [name]: next };
            emit(values, hashtags, Object.values(map).flat());
            return map;
          });
        }}
        onHashtags={(next) => {
          setHashtags(next);
          emit(values, next);
        }}
      />
      {submitError ? <p role="alert" className="rg-publisher-error">{submitError}</p> : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <button type="button" onClick={(e) => handleSubmit(e, true)} disabled={loading} className="rg-btn">
          {t('publisher.action.saveDraft' as any)}
        </button>
        <button type="submit" disabled={loading} className="rg-btn primary">
          Предпросмотр →
        </button>
      </div>
    </form>
  );
};
